import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Upload, FileSpreadsheet, Eye, Edit, Trash2, ArrowRight, 
  Package, Calendar, Building2, Hash, FileText, Search, Filter,
  CheckCircle, Clock, XCircle, RefreshCw, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Quotations() {
  const [quotations, setQuotations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [filter, setFilter] = useState({ status: '', vendor: '', search: '' });
  
  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    new_vendor: null,
    quotation_date: new Date().toISOString().split('T')[0],
    vendor_quotation_ref: '',
    items: [{ item_name: '', category: '', description: '', quantity: 1, unit_price: 0, gst_percentage: 5 }],
    notes: '',
    valid_until: ''
  });
  
  // Upload state
  const [uploadedItems, setUploadedItems] = useState([]);
  const [selectedVendorForUpload, setSelectedVendorForUpload] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // New vendor inline state
  const [useNewVendor, setUseNewVendor] = useState(false);
  const [newVendorData, setNewVendorData] = useState({ name: '', gstin: '', phone: '', address: '' });
  
  // Convert to PO state
  const [convertPoDate, setConvertPoDate] = useState(new Date().toISOString().split('T')[0]);
  const [convertNotes, setConvertNotes] = useState('');
  const [converting, setConverting] = useState(false);

  const fetchQuotations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.vendor) params.append('vendor_id', filter.vendor);
      
      const res = await axios.get(`${API_URL}/quotations?${params}`);
      let data = res.data;
      
      if (filter.search) {
        const search = filter.search.toLowerCase();
        data = data.filter(q => 
          q.quotation_number.toLowerCase().includes(search) ||
          q.vendor_name.toLowerCase().includes(search) ||
          q.items.some(i => i.item_name.toLowerCase().includes(search))
        );
      }
      
      setQuotations(data);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}/vendors`);
      setVendors(res.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  useEffect(() => {
    fetchQuotations();
    fetchVendors();
  }, [fetchQuotations]);

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_name: '', category: '', description: '', quantity: 1, unit_price: 0, gst_percentage: 5 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleCreateQuotation = async () => {
    try {
      const payload = {
        ...formData,
        vendor_id: useNewVendor ? null : formData.vendor_id,
        new_vendor: useNewVendor ? newVendorData : null,
        items: formData.items.filter(i => i.item_name && i.quantity > 0)
      };
      
      if (!payload.vendor_id && !payload.new_vendor) {
        toast.error('Please select or create a vendor');
        return;
      }
      
      if (payload.items.length === 0) {
        toast.error('Please add at least one item');
        return;
      }
      
      await axios.post(`${API_URL}/quotations`, payload);
      toast.success('Quotation created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedVendorForUpload) {
        formData.append('vendor_id', selectedVendorForUpload);
      }
      
      const res = await axios.post(`${API_URL}/quotations/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedItems(res.data.items);
      toast.success(`Extracted ${res.data.items_extracted} items from ${res.data.filename}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveUploadedQuotation = async () => {
    try {
      const payload = {
        vendor_id: useNewVendor ? null : selectedVendorForUpload,
        new_vendor: useNewVendor ? newVendorData : null,
        quotation_date: formData.quotation_date,
        vendor_quotation_ref: formData.vendor_quotation_ref,
        items: uploadedItems.filter(i => i.item_name && i.quantity > 0),
        notes: formData.notes,
        valid_until: formData.valid_until
      };
      
      await axios.post(`${API_URL}/quotations`, payload);
      toast.success('Quotation saved successfully');
      setShowUploadDialog(false);
      setUploadedItems([]);
      resetForm();
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quotation');
    }
  };

  const handleConvertToPO = async () => {
    if (!selectedQuotation) return;
    
    setConverting(true);
    try {
      const params = new URLSearchParams();
      params.append('po_date', convertPoDate);
      if (convertNotes) params.append('notes', convertNotes);
      
      const res = await axios.post(
        `${API_URL}/quotations/${selectedQuotation.id}/convert-to-po?${params}`
      );
      
      toast.success(res.data.message);
      setShowConvertDialog(false);
      setShowViewDialog(false);
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to PO');
    } finally {
      setConverting(false);
    }
  };

  const handleDeleteQuotation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    
    try {
      await axios.delete(`${API_URL}/quotations/${id}`);
      toast.success('Quotation deleted');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      new_vendor: null,
      quotation_date: new Date().toISOString().split('T')[0],
      vendor_quotation_ref: '',
      items: [{ item_name: '', category: '', description: '', quantity: 1, unit_price: 0, gst_percentage: 5 }],
      notes: '',
      valid_until: ''
    });
    setUseNewVendor(false);
    setNewVendorData({ name: '', gstin: '', phone: '', address: '' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      converted_to_po: 'bg-green-100 text-green-700',
      expired: 'bg-amber-100 text-amber-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    const icons = {
      draft: <Clock className="w-3 h-3 mr-1" />,
      converted_to_po: <CheckCircle className="w-3 h-3 mr-1" />,
      expired: <XCircle className="w-3 h-3 mr-1" />,
      cancelled: <XCircle className="w-3 h-3 mr-1" />
    };
    const labels = {
      draft: 'Draft',
      converted_to_po: 'Converted to PO',
      expired: 'Expired',
      cancelled: 'Cancelled'
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  const calculateTotals = (items) => {
    let taxable = 0, gst = 0;
    items.forEach(item => {
      const t = (item.quantity || 0) * (item.unit_price || 0);
      taxable += t;
      gst += t * ((item.gst_percentage || 5) / 100);
    });
    return { taxable, gst, total: taxable + gst };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Quotations</h1>
          <p className="text-slate-500 text-sm">Manage vendor quotations and convert to Purchase Orders</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowUploadDialog(true)}
            variant="outline"
            className="border-teal-600 text-teal-700 hover:bg-teal-50"
            data-testid="upload-quotation-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Excel
          </Button>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="create-quotation-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Quotation
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search quotations..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Select value={filter.status || "all"} onValueChange={(v) => setFilter(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="converted_to_po">Converted to PO</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter.vendor || "all"} onValueChange={(v) => setFilter(prev => ({ ...prev, vendor: v === "all" ? "" : v }))}>
              <SelectTrigger>
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchQuotations}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quotations List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      ) : quotations.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No quotations found</h3>
            <p className="text-slate-400 text-sm mt-1">Upload an Excel file or create a new quotation manually</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {quotations.map((q) => (
            <Card key={q.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`quotation-card-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-teal-700">{q.quotation_number}</span>
                      {getStatusBadge(q.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {q.vendor_name}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {q.quotation_date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Package className="w-4 h-4 text-slate-400" />
                        {q.items.length} items
                      </div>
                      <div className="font-medium text-slate-800">
                        ₹{q.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                    {q.converted_po_number && (
                      <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                        <ArrowRight className="w-4 h-4" />
                        Converted to: {q.converted_po_number}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedQuotation(q); setShowViewDialog(true); }}
                      data-testid={`view-quotation-${q.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {q.status === 'draft' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={() => { 
                            setSelectedQuotation(q); 
                            setConvertPoDate(new Date().toISOString().split('T')[0]);
                            setShowConvertDialog(true); 
                          }}
                          data-testid={`convert-quotation-${q.id}`}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Convert to PO
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteQuotation(q.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Quotation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Create New Quotation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Vendor Selection */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <div className="flex items-center gap-4">
                <Label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!useNewVendor}
                    onChange={() => setUseNewVendor(false)}
                    className="accent-teal-600"
                  />
                  Existing Vendor
                </Label>
                <Label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={useNewVendor}
                    onChange={() => setUseNewVendor(true)}
                    className="accent-teal-600"
                  />
                  New Vendor
                </Label>
              </div>
              
              {!useNewVendor ? (
                <Select value={formData.vendor_id} onValueChange={(v) => setFormData(prev => ({ ...prev, vendor_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Vendor Name *"
                    value={newVendorData.name}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <Input
                    placeholder="GSTIN"
                    value={newVendorData.gstin}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, gstin: e.target.value }))}
                  />
                  <Input
                    placeholder="Phone"
                    value={newVendorData.phone}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                  <Input
                    placeholder="Address"
                    value={newVendorData.address}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>
              )}
            </div>
            
            {/* Quotation Details */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quotation Date *</Label>
                <Input
                  type="date"
                  value={formData.quotation_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, quotation_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Vendor Ref #</Label>
                <Input
                  placeholder="Vendor's quotation number"
                  value={formData.vendor_quotation_ref}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_quotation_ref: e.target.value }))}
                />
              </div>
              <div>
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-base font-semibold">Items</Label>
                <Button size="sm" variant="outline" onClick={handleAddItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Item Name</th>
                      <th className="px-3 py-2 text-left font-medium">Category</th>
                      <th className="px-3 py-2 text-right font-medium w-20">Qty</th>
                      <th className="px-3 py-2 text-right font-medium w-28">Unit Price</th>
                      <th className="px-3 py-2 text-right font-medium w-20">GST %</th>
                      <th className="px-3 py-2 text-right font-medium w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, idx) => {
                      const total = item.quantity * item.unit_price * (1 + item.gst_percentage / 100);
                      return (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">
                            <Input
                              placeholder="Item name"
                              value={item.item_name}
                              onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                              className="h-8"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              placeholder="Category"
                              value={item.category}
                              onChange={(e) => handleItemChange(idx, 'category', e.target.value)}
                              className="h-8"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', parseInt(e.target.value) || 0)}
                              className="h-8 text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="h-8 text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              min="0"
                              max="28"
                              value={item.gst_percentage}
                              onChange={(e) => handleItemChange(idx, 'gst_percentage', parseFloat(e.target.value) || 0)}
                              className="h-8 text-right"
                            />
                          </td>
                          <td className="px-3 py-1 text-right font-medium">
                            ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-2 py-1">
                            {formData.items.length > 1 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveItem(idx)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  {(() => {
                    const totals = calculateTotals(formData.items);
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Taxable Amount:</span>
                          <span>₹{totals.taxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">GST:</span>
                          <span>₹{totals.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between font-semibold text-base border-t pt-1">
                          <span>Grand Total:</span>
                          <span className="text-teal-700">₹{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full h-20 px-3 py-2 border rounded-lg text-sm resize-none"
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateQuotation} className="bg-teal-600 hover:bg-teal-700">
              Create Quotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Upload Quotation from Excel
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Vendor Selection */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <div className="flex items-center gap-4">
                <Label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!useNewVendor}
                    onChange={() => setUseNewVendor(false)}
                    className="accent-teal-600"
                  />
                  Existing Vendor
                </Label>
                <Label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={useNewVendor}
                    onChange={() => setUseNewVendor(true)}
                    className="accent-teal-600"
                  />
                  New Vendor
                </Label>
              </div>
              
              {!useNewVendor ? (
                <Select value={selectedVendorForUpload} onValueChange={setSelectedVendorForUpload}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Vendor Name *"
                    value={newVendorData.name}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, name: e.target.value }))}
                  />
                  <Input
                    placeholder="GSTIN"
                    value={newVendorData.gstin}
                    onChange={(e) => setNewVendorData(prev => ({ ...prev, gstin: e.target.value }))}
                  />
                </div>
              )}
            </div>
            
            {/* Upload Area */}
            {uploadedItems.length === 0 ? (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-2">Upload an Excel file (.xlsx) with quotation items</p>
                <p className="text-slate-500 text-sm mb-4">
                  Expected columns: Category, Description, Qty, List Price, Offer Price, GST %
                </p>
                <input
                  type="file"
                  id="excel-upload"
                  accept=".xlsx,.xls"
                  onChange={handleUploadExcel}
                  className="hidden"
                  data-testid="excel-file-input"
                />
                <div className="flex justify-center gap-3">
                  <Button 
                    variant="outline" 
                    disabled={uploading}
                    onClick={() => document.getElementById('excel-upload').click()}
                    data-testid="choose-file-btn"
                  >
                    {uploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Choose File
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                    onClick={() => {
                      window.open(`${API_URL}/quotations/template`, '_blank');
                    }}
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Quotation Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Quotation Date *</Label>
                    <Input
                      type="date"
                      value={formData.quotation_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, quotation_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Vendor Ref #</Label>
                    <Input
                      placeholder="Vendor's quotation number"
                      value={formData.vendor_quotation_ref}
                      onChange={(e) => setFormData(prev => ({ ...prev, vendor_quotation_ref: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Valid Until</Label>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                    />
                  </div>
                </div>
                
                {/* Extracted Items Table */}
                <div>
                  <Label className="text-base font-semibold mb-2 block">
                    Extracted Items ({uploadedItems.length})
                  </Label>
                  <div className="border rounded-lg overflow-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Item Name</th>
                          <th className="px-3 py-2 text-left font-medium">Category</th>
                          <th className="px-3 py-2 text-right font-medium w-20">Qty</th>
                          <th className="px-3 py-2 text-right font-medium w-28">Unit Price</th>
                          <th className="px-3 py-2 text-right font-medium w-20">GST %</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadedItems.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-1">
                              <Input
                                value={item.item_name}
                                onChange={(e) => {
                                  const updated = [...uploadedItems];
                                  updated[idx].item_name = e.target.value;
                                  setUploadedItems(updated);
                                }}
                                className="h-8"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                value={item.category || ''}
                                onChange={(e) => {
                                  const updated = [...uploadedItems];
                                  updated[idx].category = e.target.value;
                                  setUploadedItems(updated);
                                }}
                                className="h-8"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => {
                                  const updated = [...uploadedItems];
                                  updated[idx].quantity = parseInt(e.target.value) || 0;
                                  setUploadedItems(updated);
                                }}
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const updated = [...uploadedItems];
                                  updated[idx].unit_price = parseFloat(e.target.value) || 0;
                                  setUploadedItems(updated);
                                }}
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                min="0"
                                max="28"
                                value={item.gst_percentage}
                                onChange={(e) => {
                                  const updated = [...uploadedItems];
                                  updated[idx].gst_percentage = parseFloat(e.target.value) || 0;
                                  setUploadedItems(updated);
                                }}
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setUploadedItems(prev => prev.filter((_, i) => i !== idx))}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-1 text-sm">
                    {(() => {
                      const totals = calculateTotals(uploadedItems);
                      return (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Taxable Amount:</span>
                            <span>₹{totals.taxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">GST:</span>
                            <span>₹{totals.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between font-semibold text-base border-t pt-1">
                            <span>Grand Total:</span>
                            <span className="text-teal-700">₹{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadedItems([]); resetForm(); }}>
              Cancel
            </Button>
            {uploadedItems.length > 0 && (
              <Button onClick={handleSaveUploadedQuotation} className="bg-teal-600 hover:bg-teal-700">
                Save Quotation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Quotation Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quotation-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-600" />
                {selectedQuotation?.quotation_number}
              </span>
              {selectedQuotation && getStatusBadge(selectedQuotation.status)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Vendor</p>
                  <p className="font-medium">{selectedQuotation.vendor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Date</p>
                  <p className="font-medium">{selectedQuotation.quotation_date}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Vendor Ref</p>
                  <p className="font-medium">{selectedQuotation.vendor_quotation_ref || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Valid Until</p>
                  <p className="font-medium">{selectedQuotation.valid_until || '-'}</p>
                </div>
              </div>
              
              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">#</th>
                      <th className="px-3 py-2 text-left font-medium">Item</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Unit Price</th>
                      <th className="px-3 py-2 text-right font-medium">GST %</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuotation.items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <p className="font-medium">{item.item_name}</p>
                          {item.category && <p className="text-xs text-slate-500">{item.category}</p>}
                        </td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">₹{item.unit_price?.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right">{item.gst_percentage}%</td>
                        <td className="px-3 py-2 text-right font-medium">₹{item.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-medium">
                    <tr className="border-t">
                      <td colSpan="5" className="px-3 py-2 text-right">Taxable Amount:</td>
                      <td className="px-3 py-2 text-right">₹{selectedQuotation.total_taxable?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                      <td colSpan="5" className="px-3 py-2 text-right">GST:</td>
                      <td className="px-3 py-2 text-right">₹{selectedQuotation.total_gst?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                    <tr className="border-t text-base">
                      <td colSpan="5" className="px-3 py-2 text-right font-semibold">Grand Total:</td>
                      <td className="px-3 py-2 text-right font-semibold text-teal-700">₹{selectedQuotation.grand_total?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* Notes */}
              {selectedQuotation.notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-700 uppercase mb-1">Notes</p>
                  <p className="text-sm">{selectedQuotation.notes}</p>
                </div>
              )}
              
              {/* Converted PO Info */}
              {selectedQuotation.converted_po_number && (
                <div className="p-3 bg-green-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-700 uppercase mb-1">Converted to Purchase Order</p>
                    <p className="font-medium text-green-800">{selectedQuotation.converted_po_number}</p>
                  </div>
                  <Button variant="outline" size="sm" className="border-green-600 text-green-700">
                    <Eye className="w-4 h-4 mr-1" />
                    View PO
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            {selectedQuotation?.status === 'draft' && (
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => { 
                  setConvertPoDate(new Date().toISOString().split('T')[0]);
                  setShowConvertDialog(true); 
                }}
              >
                <ArrowRight className="w-4 h-4 mr-1" />
                Convert to PO
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to PO Confirmation Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-teal-600" />
              Convert to Purchase Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will create a new Purchase Order from quotation <strong>{selectedQuotation?.quotation_number}</strong> with all items copied.
            </p>
            
            <div>
              <Label>PO Date</Label>
              <Input
                type="date"
                value={convertPoDate}
                onChange={(e) => setConvertPoDate(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Additional Notes</Label>
              <textarea
                value={convertNotes}
                onChange={(e) => setConvertNotes(e.target.value)}
                className="w-full h-20 px-3 py-2 border rounded-lg text-sm resize-none"
                placeholder="Any notes for the PO..."
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <strong>Note:</strong> The PO will not require batch numbers or sizes. These details will be entered when receiving goods.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToPO} 
              disabled={converting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {converting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create PO
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
